import React, { useCallback, useRef, useState } from "react";
import { Button } from "@mui/material";
import { File, Folder } from "lucide-react";
import FileUploadIcon from "@/assets/file-upload.svg";
import { readDroppedFolder } from "../../utils/readDroppedFolder";
import FpButton from "../../FpButton";

const HiddenInput = ({
  onChooseFiles,
  multiple,
  selectables,
  kind,
  inputRef,
}: {
  onChooseFiles: (files: File[]) => void;
  multiple: boolean;
  selectables: ("file" | "folder")[];
  kind: "button" | "drag-drop";
  inputRef?: React.RefObject<HTMLInputElement>;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  // Use the passed ref if provided, otherwise use the local ref
  const inputRefToUse = inputRef || ref;

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (!fileList) return;

      const files = [...fileList];
      onChooseFiles(files);
    },
    [onChooseFiles]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const items = e.dataTransfer.items;

      if (items) {
        const entries = Array.from(items).map((item) =>
          item.webkitGetAsEntry()
        );
        const hasFolder = entries.some((entry) => entry && entry.isDirectory);

        if (hasFolder) {
          // Handle folder drop
          readDroppedFolder(items).then((files) => {
            onChooseFiles(files);
          });
        } else {
          // Handle file drop
          const files = e.dataTransfer.files;
          onChooseFiles(Array.from(files));
        }
      }
    },
    [onChooseFiles]
  );

  return (
    <>
      <input
        type="file"
        ref={inputRefToUse}
        onChange={handleFileChange}
        style={{ display: "none" }}
        multiple={multiple}
        webkitdirectory={selectables.includes("folder") ? "" : undefined}
        directory={selectables.includes("folder") ? "" : undefined}
      />
      {kind === "button" && (
        <FpButton
          variant="outlined"
          onClick={() => inputRefToUse.current?.click()}
          style={{ padding: "3px 8px" }}
        >
          {selectables.includes("file") ? (
            <File className="w-3 h-3 mr-2" />
          ) : (
            <Folder className="w-3 h-3 mr-2" />
          )}
          Choose{" "}
          {selectables.includes("file")
            ? "File" + (multiple ? "s" : "")
            : "Folder"}
        </FpButton>
      )}
      {kind === "drag-drop" && (
        <div
          onDragEnter={() => setDragActive(true)}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          style={{
            border: dragActive ? "2px dashed #2196f3" : "2px dashed #aaaaaa",
            backgroundColor: dragActive ? "#e3f2fd" : "#eee",
          }}
          className="flex justify-center flex-col items-center w-full h-[200px] rounded-md p-4 text-gray-600 cursor-auto"
          // onClick={() => ref.current?.click()}
        >
          <img src={FileUploadIcon} alt="" className="w-[25px] opacity-80" />
          <div className="mt-4 w-[300px] flex justify-center text-[12px]">
            {(() => {
              const key = selectables.join("-") + "-" + multiple.toString();
              switch (key) {
                case "file-true":
                  return "Drop Files here";
                case "file-false":
                  return "Drop File here";
                case "folder-true":
                case "folder-false":
                  return "Drop Folder here";
                case "file-folder-true":
                  return "Drop Files or Folder here";
                case "file-folder-false":
                  return "Drop File or Folder here";
                default:
                  return "Drop Files here";
              }
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default HiddenInput;
